import { Container, Grid, Typography, Paper, Button, FormControl, Select, MenuItem } from "@mui/material"
import globals from "../../globals"
import { useEffect, useMemo, useState } from "react"
import { useNavigate } from "react-router-dom"

const SORT_OPTIONS = [
    { value: 'newest', label: 'най-новите първо' },
    { value: 'oldest', label: 'най-старите първо' },
    { value: 'alpha', label: 'по азбучен ред' },
    { value: 'upcoming', label: 'предстоящите първо' },
];

const formatDate = (value) => {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    const dd = String(date.getDate()).padStart(2, '0');
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    return `${dd}.${mm}.${date.getFullYear()}`;
};

const isPast = (webinar) => Boolean(webinar.date) && new Date(webinar.date).getTime() < Date.now();

const WebinarsPage = () => {

    const navigate = useNavigate();
    const [webinars, setWebinars] = useState([]);
    const [sort, setSort] = useState('newest');

    useEffect(() => {
        fetch(globals.apiUrl + '/webinars/', {
            headers: { 'Content-Type': 'application/json' },
        })
            .then(response => response.json())
            .then(data => setWebinars(Array.isArray(data) ? data : []))
            .catch(() => setWebinars([]));
    }, []);

    const sortedWebinars = useMemo(() => {
        const time = (w) => (w.date ? new Date(w.date).getTime() : null);
        const arr = [...webinars];
        switch (sort) {
            case 'oldest':
                arr.sort((a, b) => (time(a) ?? Infinity) - (time(b) ?? Infinity));
                break;
            case 'alpha':
                arr.sort((a, b) => (a.title || '').localeCompare(b.title || '', 'bg'));
                break;
            case 'upcoming':
                arr.sort((a, b) => {
                    const ap = isPast(a) ? 1 : 0;
                    const bp = isPast(b) ? 1 : 0;
                    if (ap !== bp) return ap - bp; // upcoming first
                    return (time(a) ?? Infinity) - (time(b) ?? Infinity); // soonest first
                });
                break;
            case 'newest':
            default:
                arr.sort((a, b) => (time(b) ?? -Infinity) - (time(a) ?? -Infinity));
                break;
        }
        return arr;
    }, [webinars, sort]);

    return (
        <Container sx={{ mb: 3 }}>
            <Grid container justifyContent={'center'} direction={'column'} textAlign={'center'} sx={{ mt: 2 }}>
                <Grid item>
                    <Typography sx={{ color: globals.colors.lightRed, fontWeight: 'bold', fontSize: 34 }}>Уебинари</Typography>
                </Grid>
                <Grid item>
                    <Typography sx={{ fontWeight: 'bold', color: globals.colors.darkBlue, fontSize: 18, mt: 2 }}>
                        Бъди част от нашите изцяло онлайн събития.<br />
                        Следи предстоящите уебинари и се включвай в обучения по теми, които са важни за теб.
                    </Typography>
                </Grid>
            </Grid>

            <Grid container justifyContent={'center'} sx={{ mt: 4 }}>
                <FormControl size="small">
                    <Select
                        value={sort}
                        onChange={(e) => setSort(e.target.value)}
                        sx={{
                            backgroundColor: globals.colors.lightPink,
                            borderRadius: 3,
                            fontWeight: 'bold',
                            color: globals.colors.darkBlue,
                            '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
                            '& .MuiSvgIcon-root': { color: globals.colors.darkBlue },
                        }}
                        renderValue={(value) => `Подреди по (${SORT_OPTIONS.find((o) => o.value === value)?.label})`}
                    >
                        {SORT_OPTIONS.map((option) => (
                            <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Grid>

            <Grid container justifyContent={'center'} spacing={4} sx={{ mt: 1 }}>
                {sortedWebinars.length === 0 && (
                    <Grid item xs={12} textAlign={'center'} sx={{ mt: 5 }}>
                        <Typography sx={{ color: globals.colors.gray }}>Очаквайте скоро нови уебинари.</Typography>
                    </Grid>
                )}
                {sortedWebinars.map((webinar) => {
                    const past = isPast(webinar);
                    const detailUrl = `/webinar/${webinar.slug || webinar.id}`;
                    return (
                        <Grid item key={webinar.id} xs={12} sm={6} display="flex" justifyContent="center">
                            <Paper elevation={3} sx={{ width: '100%', maxWidth: 380, borderRadius: 2, overflow: 'hidden', textAlign: 'center', pb: 2 }}>
                                <img
                                    src={webinar.cover_image_url || webinar.banner_image_url || 'webinar.png'}
                                    style={{ width: '100%', height: 210, objectFit: 'cover', display: 'block' }}
                                    alt={webinar.title || webinar.id}
                                />
                                <Typography sx={{
                                    mt: 2, fontSize: 14, fontWeight: 'bold',
                                    color: past ? globals.colors.gray : globals.colors.lightRed,
                                }}>
                                    {past ? 'Приключил' : 'Предстои'}
                                </Typography>
                                <Typography sx={{ mt: 0.5, fontSize: 18, fontWeight: 'bold', color: globals.colors.darkBlue, px: 2 }}>
                                    {webinar.title || '(без заглавие)'}
                                </Typography>
                                {webinar.date && (
                                    <Typography sx={{ mt: 1, fontSize: 14, fontWeight: 'bold', color: globals.colors.darkBlue }}>
                                        {formatDate(webinar.date)}
                                    </Typography>
                                )}
                                <Button
                                    variant="contained"
                                    onClick={() => navigate(detailUrl)}
                                    sx={{
                                        mt: 2, px: 4, borderRadius: 2, textTransform: 'none', fontSize: 15,
                                        backgroundColor: globals.colors.lightRed,
                                        '&:hover': { backgroundColor: globals.colors.darkRed },
                                    }}
                                >
                                    Прочети повече
                                </Button>
                            </Paper>
                        </Grid>
                    );
                })}
            </Grid>
        </Container>
    )
}

export default WebinarsPage
